import React from 'react';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {HeatmapImage} from '../../../connectivity-guru.contracts';
import {ImageCarousel} from './ImageCarousel';
import {arrow, errorOutline} from '../../icons';
import {observer} from 'mobx-react';
import {ITranslate} from '../../../../../services/LocalizationService';
import {WifiHeatmapPreviewComponent} from './WifiHeatmapPreviewComponent';
import {LayeredImageController} from '../../../../../shared/image-layers/LayeredImageController';

interface HeatmapImagesProps {
    imageUrls: HeatmapImage[];
    baseUrl?: string;
    onImageClick?: (imageUrl: string, imageLabel?: string) => void;
    translate: ITranslate;
    layeredImageController: LayeredImageController;
}

@observer
export class HeatmapImages extends TechseeBaseComponent<HeatmapImagesProps> {
    renderInternal(): JSX.Element {
        const {imageUrls, onImageClick, translate, layeredImageController} = this.props;

        const ssidHeatmaps = imageUrls.filter((img) => img.type === 'ssid_heatmap');
        const bssidHeatmaps = imageUrls.filter((img) => img.type === 'bssid_heatmap');

        const hasDisplayableHeatmaps = ssidHeatmaps.length > 0 || bssidHeatmaps.length > 0;

        if (!hasDisplayableHeatmaps) {
            return (
                <div className='heatmap-images-container'>
                    <div className='scan-result-failed-content'>
                        <div className='scan-result-failed-icon'>{errorOutline}</div>
                        <div className='scan-result-failed-text'>
                            <p>{translate('CONNECTIVITY_GURU.VISUAL_SCAN.SCAN_FAILED_LINE1')}</p>
                            <p>{translate('CONNECTIVITY_GURU.VISUAL_SCAN.SCAN_FAILED_LINE2')}</p>
                        </div>
                    </div>
                </div>
            );
        }

        const bssidCarouselImages = bssidHeatmaps.map((img, index) => ({
            id: `bssid-${index}`,
            imageUrl: img.imageUrl,
            label: img.bssid
        }));

        const renderBssidImage = (img: {id: string; imageUrl: string; label?: string}, index: number) => {
            const handleClick = (e: React.MouseEvent) => {
                if (onImageClick) {
                    onImageClick(img.imageUrl, img.label);
                }
            };

            return (
                <div key={img.id} className='heatmap-image-small clickable' onClick={handleClick}>
                    <img src={img.imageUrl} alt={`BSSID Heatmap - ${img.label || ''}`} />
                    {img.label && <div className='heatmap-label'>{img.label}</div>}
                </div>
            );
        };

        return (
            <div className='heatmap-images-container'>
                {/* First row - BSSID Heatmaps */}
                {bssidHeatmaps.length > 1 ? (
                    <div className='heatmap-row heatmap-row-bssid'>
                        <ImageCarousel
                            images={bssidCarouselImages}
                            itemsPerSlide={3}
                            transitionTime={500}
                            animationHandler='slide'
                            renderImage={renderBssidImage}
                            arrowIcon={arrow}
                        />
                    </div>
                ) : null}

                {ssidHeatmaps.length > 0 && (
                    <div className='heatmap-row heatmap-row-ssid'>
                        {ssidHeatmaps.map((img, index) => {
                            const handleClick = (e: React.MouseEvent) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (onImageClick) {
                                    onImageClick(img.imageUrl, img.ssid);
                                }
                            };

                            return (
                                <WifiHeatmapPreviewComponent
                                    key={`ssid-${index}`}
                                    layeredImageController={layeredImageController}
                                    className='heatmap-image-large clickable'
                                    onClick={handleClick}>
                                    <img
                                        src={img.imageUrl}
                                        alt={`SSID Heatmap - ${img.ssid || ''}`}
                                        crossOrigin='anonymous'
                                    />
                                    {img.ssid && (
                                        <div className='heatmap-label'>
                                            {translate('CONNECTIVITY_GURU.VISUAL_SCAN.CONNECTED_NETWORK')} {img.ssid}
                                        </div>
                                    )}
                                </WifiHeatmapPreviewComponent>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }
}
